import { useState } from "react";
import { ArrowRight, Download } from "lucide-react";
import api, { cvUrl, formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/i18n";
import Reveal, { Cross } from "@/components/Reveal";

export default function Contact() {
  const { t } = useI18n();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setState("sending");
    setError("");
    try {
      await api.post("/contact", form);
      setState("sent");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
      setState("idle");
    }
  };

  const inputCls = "w-full border border-line bg-transparent px-4 py-3 text-sm text-white placeholder-zinc-600 outline-none transition-colors duration-200 focus:border-gold";

  return (
    <section id="contact" className="relative scroll-mt-20 border-t border-line px-6 py-24 md:px-10 md:py-32 xl:px-16" data-testid="contact-section">
      <Cross className="left-6 top-16 hidden md:block" />
      <div className="mx-auto grid max-w-[1440px] gap-14 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <Reveal>
            <div className="text-[11px] tracking-[0.3em] text-gold">{t.contact.label}</div>
            <h2 className="mt-5 font-display text-3xl font-bold leading-tight text-white md:text-4xl" data-testid="contact-title">{t.contact.title}</h2>
            <p className="mt-6 max-w-md text-sm leading-relaxed text-zinc-400" data-testid="contact-sub">{t.contact.sub}</p>
            <div className="mt-8 space-y-1.5 text-[11px] tracking-[0.25em] text-zinc-500">
              <div>{t.contact.direct}:</div>
              <div className="text-gold" data-testid="contact-email">{t.contact.emailVal}</div>
              <div className="text-gold" data-testid="contact-phone">{t.contact.phone}</div>
            </div>
            <a
              href={cvUrl()}
              download="CALEB_DEMISEW_ZELEKE_CV.pdf"
              target="_blank"
              rel="noreferrer"
              data-testid="contact-cv-btn"
              className="mt-8 inline-flex items-center gap-3 border border-gold/40 px-6 py-3 text-xs font-bold tracking-[0.2em] text-gold transition-colors duration-200 hover:bg-gold hover:text-black"
            >
              {t.contact.cvBtn}
              <Download size={14} />
            </a>
          </Reveal>
          <Reveal delay={0.15} className="mt-12 border border-line bg-panel" data-testid="contact-meta">
            <div className="divide-y divide-white/5">
              {t.contact.meta.map((row) => (
                <div key={row.k} className="flex items-center justify-between px-5 py-3.5 text-xs tracking-[0.15em]">
                  <span className="text-zinc-500">{row.k}</span>
                  <span className="text-zinc-200">{row.v}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            {state === "sent" ? (
              <div className="flex h-full min-h-[320px] flex-col items-start justify-center border border-gold/40 bg-panel p-10" data-testid="contact-success">
                <div className="text-xs tracking-[0.25em] text-gold">{t.contact.sent}</div>
                <span className="mt-4 inline-block h-4 w-2.5 bg-gold blink" />
              </div>
            ) : (
              <form onSubmit={submit} className="border border-line bg-panel p-6 md:p-10" data-testid="contact-form">
                <div className="grid gap-6 md:grid-cols-2">
                  <div>
                    <label className="text-[10px] tracking-[0.3em] text-zinc-500">{t.contact.form.name}</label>
                    <input required value={form.name} onChange={set("name")} data-testid="contact-name-input" className={`mt-2 ${inputCls}`} placeholder="> _" />
                  </div>
                  <div>
                    <label className="text-[10px] tracking-[0.3em] text-zinc-500">{t.contact.form.email}</label>
                    <input required type="email" value={form.email} onChange={set("email")} data-testid="contact-email-input" className={`mt-2 ${inputCls}`} placeholder="> _" />
                  </div>
                </div>
                <div className="mt-6">
                  <label className="text-[10px] tracking-[0.3em] text-zinc-500">{t.contact.form.message}</label>
                  <textarea required rows={6} value={form.message} onChange={set("message")} data-testid="contact-message-input" className={`mt-2 ${inputCls} resize-none`} placeholder="> _" />
                </div>
                {error && <div className="mt-4 border border-red-500/40 bg-red-500/10 px-4 py-3 text-xs text-red-400" data-testid="contact-error">{error}</div>}
                <button
                  type="submit"
                  disabled={state === "sending"}
                  data-testid="contact-send-btn"
                  className="group mt-8 flex items-center gap-3 bg-gold px-8 py-3.5 text-xs font-bold tracking-[0.25em] text-black transition-all duration-200 hover:-translate-y-0.5 disabled:opacity-60"
                >
                  {state === "sending" ? t.contact.form.sending : t.contact.form.send}
                  <ArrowRight size={14} className="transition-transform duration-200 group-hover:translate-x-1" />
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
}
